import React, { useState, useEffect } from 'react';
import { IoClose } from 'react-icons/io5';
import {  FaEnvelope } from 'react-icons/fa';
import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client (you'll need to replace these with your actual values)
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

const supabase = createClient(supabaseUrl, supabaseKey);

function NewsletterModal() {
  // Check localStorage when component mounts to decide initial state
  const [isOpen, setIsOpen] = useState(false);
  const [currentTimePKT, setCurrentTimePKT] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null, 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');


  useEffect(() => {
    // Check if user has already subscribed
    const hasSubscribed = localStorage.getItem('newsletterSubscribed') === 'true';
    
    // Only show modal if user hasn't subscribed
    setIsOpen(!hasSubscribed);
    
    // Function to update time
    const updateTime = () => {
      const now = new Date();
      setCurrentTimePKT(
        now.toLocaleString('en-PK', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          timeZoneName: 'short',
          timeZone: 'Asia/Karachi',
          hour12: true,
        })
      );
    };

    updateTime();
    const timerId = setInterval(updateTime, 1000);

    return () => clearInterval(timerId);
  }, []);

  const closeModal = () => {
    setIsOpen(false);
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handleNameChange = (e) => {
    setName(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setSubmitStatus('error');
      setErrorMessage('Please enter a valid email address');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      // Insert data into Supabase
      const { data, error } = await supabase
        .from('newsletter_subscribers')
        .insert([
          { email, name, subscribed_at: new Date().toISOString() }
        ]);
        
      if (error) throw error;
      
      // Success - set localStorage to remember subscription
      localStorage.setItem('newsletterSubscribed', 'true');
      
      setSubmitStatus('success');
      setEmail('');
      setName('');
      
      // After 3 seconds, close the modal
      setTimeout(() => {
        closeModal();
      }, 3000);
      
    } catch (error) {
      console.error('Error submitting to Supabase:', error);
      setSubmitStatus('error');
      setErrorMessage(error.message || 'Failed to subscribe. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
      <div className="relative w-full max-w-lg p-6 mx-4 bg-white rounded-xl shadow-2xl transform transition-all duration-300 ease-out scale-100">
        {/* Close Button */}
        <button
          onClick={closeModal}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 transition-colors p-1 rounded-full hover:bg-gray-200 focus:outline-none"
          aria-label="Close modal"
        >
          <IoClose size={28} />
        </button>

        {/* Newsletter Icon */}
        <div className="flex justify-center mb-4">
          <FaEnvelope size={60} className="text-yellow-500" />
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-3">
          Subscribe to Our Newsletter!
        </h2>

        {/* Main Message */}
        <p className="text-center text-gray-600 mb-5">
          Stay updated with our latest news, articles, and special offers.
        </p>

        {/* Subscription Form */}
        <form onSubmit={handleSubmit} className="mb-6">
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={handleNameChange}
              className="w-full px-4 py-2 text-black border border-gray-300 rounded-md shadow-sm focus:ring-yellow-500 focus:border-yellow-500"
              placeholder="Your Name"
            />
          </div>
          
          <div className="mb-6">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={handleEmailChange}
              required
              className="w-full px-4 text-black py-2 border border-gray-300 rounded-md shadow-sm focus:ring-yellow-500 focus:border-yellow-500"
              placeholder="your@email.com"
            />
          </div>

          {/* Status Messages */}
          {submitStatus === 'success' && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
              Thank you for subscribing! You'll hear from us soon.
            </div>
          )}
          
          {submitStatus === 'error' && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3 px-4 bg-yellow-500 text-black font-semibold rounded-lg shadow-md hover:bg-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition-all duration-200"
          >
            {isSubmitting ? 'Subscribing...' : 'Subscribe Now'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default NewsletterModal;